import React, { createContext, useCallback, useContext, useState } from 'react'
import { DateTime } from 'luxon'
import { useGTLocalStorage } from '../../hooks'
import { TEvent } from '../../utils/types'
import { emptyFunction } from '../../utils/utils'
import { TCalendarType } from '../views/CalendarView'

export interface ContextValues {
    date: DateTime
    dayViewDate: DateTime
    calendarType: TCalendarType
    showMainHeader: boolean
    showDateHeader: boolean
    isCollapsed: boolean
    isTaskDraggingOverDetailsView: boolean
    selectedEvent: TEvent | null
    isPopoverDisabled: boolean
    isTasksDueViewCollapsed: boolean
    disableSelectEvent: boolean
    isTasksOverdueViewCollapsed: boolean
    showTaskToCalSidebar: boolean
    setDate: React.Dispatch<React.SetStateAction<DateTime>>
    setDayViewDate: React.Dispatch<React.SetStateAction<DateTime>>
    setCalendarType: React.Dispatch<React.SetStateAction<TCalendarType>>
    setShowMainHeader: React.Dispatch<React.SetStateAction<boolean>>
    setShowDateHeader: React.Dispatch<React.SetStateAction<boolean>>
    setIsCollapsed: (isCollapsed: boolean) => void
    setIsTaskDraggingOverDetailsView: (isTaskDraggingOverDetailsView: boolean) => void
    setSelectedEvent: (event: TEvent | null) => void
    setIsPopoverDisabled: React.Dispatch<React.SetStateAction<boolean>>
    setIsTasksDueViewCollapsed: React.Dispatch<React.SetStateAction<boolean>>
    setIsTasksOverdueViewCollapsed: React.Dispatch<React.SetStateAction<boolean>>
    setShowTaskToCalSidebar: (showTaskToCalSidebar: boolean) => void
    setIsTaskListPinned: (isTaskListPinned: boolean) => void
}
const CalendarContext = createContext<ContextValues>({
    date: DateTime.now(),
    dayViewDate: DateTime.now(),
    calendarType: 'day',
    showMainHeader: true,
    showDateHeader: true,
    isCollapsed: false,
    isTaskDraggingOverDetailsView: false,
    selectedEvent: null,
    isPopoverDisabled: false,
    isTasksDueViewCollapsed: false,
    disableSelectEvent: false,
    isTasksOverdueViewCollapsed: false,
    showTaskToCalSidebar: false,
    setDate: emptyFunction,
    setDayViewDate: emptyFunction,
    setCalendarType: emptyFunction,
    setShowMainHeader: emptyFunction,
    setShowDateHeader: emptyFunction,
    setIsCollapsed: emptyFunction,
    setIsTaskDraggingOverDetailsView: emptyFunction,
    setSelectedEvent: emptyFunction,
    setIsPopoverDisabled: emptyFunction,
    setIsTasksDueViewCollapsed: emptyFunction,
    setIsTasksOverdueViewCollapsed: emptyFunction,
    setShowTaskToCalSidebar: emptyFunction,
    setIsTaskListPinned: emptyFunction,
})

interface CalendarContextProviderProps {
    children: React.ReactNode
    isPopoverDisabled?: boolean
}
export const CalendarContextProvider = (props: CalendarContextProviderProps) => {
    const [date, setDate] = useState<DateTime>(DateTime.now())
    const [dayViewDate, setDayViewDate] = useState<DateTime>(DateTime.now())
    const [calendarType, setCalendarType] = useState<TCalendarType>('day')
    const [showMainHeader, setShowMainHeader] = useState<boolean>(true)
    const [showDateHeader, setShowDateHeader] = useState<boolean>(true)
    const [isCollapsed, setIsCollapsed] = useGTLocalStorage('calendarCollapsed', false)
    const [isTaskDraggingOverDetailsView, setIsTaskDraggingOverDetailsView] = useState<boolean>(false)
    const [selectedEvent, setSelectedEvent] = useState<TEvent | null>(null)
    const [isPopoverDisabled, setIsPopoverDisabled] = useState<boolean>(!!props.isPopoverDisabled)
    const [isTasksDueViewCollapsed, setIsTasksDueViewCollapsed] = useGTLocalStorage('dueTodayCollapsed', false)
    const [isTasksOverdueViewCollapsed, setIsTasksOverdueViewCollapsed] = useGTLocalStorage('overdueCollapsed', false)
    const [storedShowTaskToCalSidebar, setStoredShowTaskToCalSidebar] = useGTLocalStorage(
        'taskToCalendarSidebar',
        false
    )
    // Ultrawide screens pin the task list for as long as they stay ultrawide (see DefaultTemplate). That
    // is a layout response, not a user choice, so it is kept out of local storage: a reload on the big
    // monitor must not leave the list forced open on the laptop. Closing the list unpins it as well.
    const [isTaskListPinned, setIsTaskListPinned] = useState<boolean>(false)
    const showTaskToCalSidebar = storedShowTaskToCalSidebar || isTaskListPinned
    const setShowTaskToCalSidebar = useCallback(
        (show: boolean) => {
            if (!show) setIsTaskListPinned(false)
            setStoredShowTaskToCalSidebar(show)
        },
        [setStoredShowTaskToCalSidebar]
    )
    const collapseAndSetType = useCallback(
        (isCollapsed: boolean) => {
            setIsCollapsed(isCollapsed)
            if (isCollapsed) {
                setCalendarType('day')
                setDate(dayViewDate)
            }
        },
        [dayViewDate, setIsCollapsed, setCalendarType]
    )

    const value = {
        date,
        dayViewDate,
        calendarType,
        showMainHeader,
        showDateHeader,
        isCollapsed,
        isTaskDraggingOverDetailsView,
        selectedEvent,
        isPopoverDisabled,
        isTasksDueViewCollapsed,
        disableSelectEvent: false,
        isTasksOverdueViewCollapsed,
        showTaskToCalSidebar,
        setDate,
        setDayViewDate,
        setCalendarType,
        setShowMainHeader,
        setShowDateHeader,
        setIsCollapsed: collapseAndSetType,
        setIsTaskDraggingOverDetailsView,
        setSelectedEvent,
        setIsPopoverDisabled,
        setIsTasksDueViewCollapsed,
        setIsTasksOverdueViewCollapsed,
        setShowTaskToCalSidebar,
        setIsTaskListPinned,
    }

    return <CalendarContext.Provider value={value}>{props.children}</CalendarContext.Provider>
}

export const useCalendarContext = () => {
    return useContext(CalendarContext)
}
